/**
 * Super-admin tenant routing for the global request interceptor (main.js).
 *
 * One origin-swap map keyed by API family. When the super admin selects a non-primary
 * tenant, any request to that family's primary host is rewritten to the tenant's host
 * (path preserved). The token interceptor then attaches the matching token by origin.
 * This is why modules need NO per-call tenant code: routing happens in one place.
 *
 * EXPANDING:
 *  - New module on an existing family (e.g. farm/user mgmt on the SaaS API): nothing to do.
 *  - Make a whole new API family tenant-aware: add one { default, kenya } entry to FAMILIES.
 */
import { TENANT_KEYS, getPrimaryTenantKey } from '@/utils/tenant/tenants';

// API families that follow the super-admin tenant switch. Per tenant key -> base host.
const FAMILIES = [
  {
    name: 'saas', // carbon-credit, farm-management, user-management — all live here
    bases: {
      [TENANT_KEYS.DEFAULT]: process.env.VUE_APP_BASE_URL,
      [TENANT_KEYS.KENYA]: process.env.VUE_APP_KENYA_BASE_URL,
    },
  },
  // To make Deforestation follow the tenant switch, add (2 lines per family):
  // { name: 'defo', bases: { [TENANT_KEYS.DEFAULT]: process.env.VUE_APP_DEFORESTATION_BASE_URL, [TENANT_KEYS.KENYA]: process.env.VUE_APP_KENYA_DEFO_BASE_URL } },
  // { name: 'reporting', bases: { [TENANT_KEYS.DEFAULT]: process.env.VUE_APP_DEFORESTRATION_REPORTING_API_BASE_URL, [TENANT_KEYS.KENYA]: process.env.VUE_APP_KENYA_REPORTING_API_BASE_URL } },
];

// Identity/auth paths never follow the tenant switch — the super admin's home is the
// primary tenant (login/refresh authenticate there; user-role is their own identity).
const TENANT_AGNOSTIC = [/\/login\/?(\?|$)/, /\/refresh/i, /\/user\/membership\/user-role/];

function originOf(url) {
  try {
    return new URL(url).origin;
  } catch (e) {
    return '';
  }
}

/**
 * Returns the rewritten absolute URL when the request should be routed to `tenantKey`,
 * or null to leave it unchanged (single-tenant, primary tenant, identity path, or a
 * host not in any tenant-aware family — e.g. marketplace/S3).
 */
export function rerouteForTenant(fullUrl, tenantKey) {
  if (!fullUrl || !tenantKey) return null;
  const primary = getPrimaryTenantKey();
  if (tenantKey === primary) return null;
  if (TENANT_AGNOSTIC.some((re) => re.test(fullUrl))) return null;

  const reqOrigin = originOf(fullUrl);
  if (!reqOrigin) return null;

  for (const fam of FAMILIES) {
    const fromOrigin = originOf(fam.bases[primary]);
    const toOrigin = originOf(fam.bases[tenantKey]);
    if (fromOrigin && toOrigin && reqOrigin === fromOrigin && toOrigin !== fromOrigin) {
      return fullUrl.replace(fromOrigin, toOrigin);
    }
  }
  return null;
}
